import React from 'react';
import { PeerRankingPanel } from '../components/panels/peers/PeerRankingPanel';
import { RadarComparisonPanel } from '../components/panels/peers/RadarComparisonPanel';
import { GrowthTrajectoriesPanel } from '../components/panels/peers/GrowthTrajectoriesPanel';
import { RiskReturnPanel } from '../components/panels/peers/RiskReturnPanel';
import { RatingMomentumPanel } from '../components/panels/peers/RatingMomentumPanel';
import { PrecedentsPanel } from '../components/panels/peers/PrecedentsPanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration
const panelConfig: PanelConfig[] = [
  { id: 'ranking', title: 'Peer Ranking', row: 1, colSpan: 4 },
  { id: 'radar', title: 'Radar Comparison', row: 2, colSpan: 2 },
  { id: 'growth', title: 'Growth Trajectories', row: 2, colSpan: 2 },
  { id: 'risk', title: 'Risk-Return', row: 3, colSpan: 2 },
  { id: 'momentum', title: 'Rating Momentum', row: 3, colSpan: 2 },
  { id: 'precedents', title: 'Precedents', row: 4, colSpan: 4 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  ranking: PeerRankingPanel,
  radar: RadarComparisonPanel,
  growth: GrowthTrajectoriesPanel,
  risk: RiskReturnPanel,
  momentum: RatingMomentumPanel,
  precedents: PrecedentsPanel,
};

const PeersView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      {/* Scrollable container for all panels */}
      <div className="flex-1 overflow-y-auto p-1 bg-terminal-border">
        <div className="flex flex-col gap-1 min-h-full">
          {/* Row 1 - Peer Ranking (full width, auto height based on content) */}
          {getOrderedPanels(1).map(panel => (
            <div
              key={panel.id}
              {...getDragProps(panel.id)}
              className={`min-h-[300px] md:min-h-[280px] transition-all duration-200 ${
                getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
              } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
            >
              {React.createElement(panelComponents[panel.id], {
                isMinimized: isPanelMinimized(panel.id),
                onMinimize: () => handleMinimize(panel.id),
              })}
            </div>
          ))}

          {/* Row 2 - Radar & Growth Trajectories (2 columns on desktop) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
            {getOrderedPanels(2).map(panel => (
              <div
                key={panel.id}
                {...getDragProps(panel.id)}
                className={`min-h-[280px] md:min-h-[250px] transition-all duration-200 ${
                  getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
                } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
              >
                {React.createElement(panelComponents[panel.id], {
                  isMinimized: isPanelMinimized(panel.id),
                  onMinimize: () => handleMinimize(panel.id),
                })}
              </div>
            ))}
          </div>

          {/* Row 3 - Risk-Return & Rating Momentum (2 columns on desktop) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
            {getOrderedPanels(3).map(panel => (
              <div
                key={panel.id}
                {...getDragProps(panel.id)}
                className={`min-h-[280px] md:min-h-[250px] transition-all duration-200 ${
                  getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
                } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
              >
                {React.createElement(panelComponents[panel.id], {
                  isMinimized: isPanelMinimized(panel.id),
                  onMinimize: () => handleMinimize(panel.id),
                })}
              </div>
            ))}
          </div>

          {/* Row 4 - Precedents (full width) */}
          {getOrderedPanels(4).map(panel => (
            <div
              key={panel.id}
              {...getDragProps(panel.id)}
              className={`min-h-[350px] md:min-h-[320px] transition-all duration-200 ${
                getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
              } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
            >
              {React.createElement(panelComponents[panel.id], {
                isMinimized: isPanelMinimized(panel.id),
                onMinimize: () => handleMinimize(panel.id),
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PeersView;
